import React, { useState, useEffect } from 'react';

/**
 * Interface representing the time units remaining in the countdown
 * @interface TimeLeft
 */
interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * Props interface for the CountdownTimer component
 * @interface CountdownTimerProps
 * @property {Date} targetDate - The target date for the countdown
 */
interface CountdownTimerProps {
  targetDate: Date;
}

/**
 * CountdownTimer Component
 * A React component that displays a countdown timer to a target date
 * with days, hours, minutes, and seconds remaining.
 * 
 * @component
 * @param {CountdownTimerProps} props - Component props
 * @returns {JSX.Element} Rendered countdown timer
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = targetDate.getTime() - new Date().getTime();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  const timeBlocks = [
    { value: timeLeft.days, label: 'Días' },
    { value: timeLeft.hours, label: 'Horas' },
    { value: timeLeft.minutes, label: 'Minutos' },
    { value: timeLeft.seconds, label: 'Segundos' }
  ];

  return (
    <div className="text-center relative z-50">
      <h3 className="text-2xl md:text-3xl font-playfair mb-8 text-wedding-burgundy tracking-wider">
        Faltan
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {timeBlocks.map((block, index) => (
          <div 
            key={index} 
            className="glass-card group p-4 md:p-6 rounded-2xl backdrop-blur-xl bg-white/10 border border-white/20 
                     transform transition-all duration-300 hover:scale-105 hover:bg-white/20
                     hover:border-wedding-burgundy/30 hover:shadow-lg hover:shadow-wedding-burgundy/10 mb-4 md:mb-0"
          >
            <div className="relative flex flex-col items-center">
              <div className="text-3xl md:text-5xl font-playfair font-semibold text-wedding-burgundy mb-2 md:mb-3
                            transform transition-all duration-300 group-hover:scale-110">
                {block.value.toString().padStart(2, '0')}
              </div>
              <div className="text-xs md:text-sm uppercase tracking-widest text-wedding-burgundy/90 font-medium
                            transform transition-all duration-300 group-hover:tracking-wider">
                {block.label}
              </div>
              <div className="absolute -inset-1 bg-gradient-to-r from-wedding-burgundy/0 via-wedding-burgundy/10 to-wedding-burgundy/0 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CountdownTimer;